import { useCallback, useEffect, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { PlatformOutletContext } from '../../app/platform-context.js'
import { ROUTES } from '../../app/routes.js'
import {
  activateOrganization,
  archiveOrganization,
  getPlatformOrganizationDetail,
  suspendOrganization,
  type PlatformLocationSummary,
  type PlatformOrganizationDetail,
} from '../../shared/api/organizations.js'
import type { OrganizationResponse } from '../../shared/types/models.js'
import { ApiError } from '../../shared/types/api.js'
import { formatDate, formatDateTime } from '../../shared/utils/format.js'
import { usePlatformApiError } from '../../app/usePlatformApiError.js'
import { isPlatformSuperadminRole } from '../../shared/utils/permissions.js'

type PageStatus = 'loading' | 'ready' | 'error' | 'notFound' | 'forbidden'
type LifecycleAction = 'suspend' | 'activate' | 'archive'

function CopyUrlButton({ url, copied, onCopy }: {
  url: string
  copied: boolean
  onCopy: (url: string) => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className="btn btn-ghost"
      aria-label={t('platform.organizationDetail.copyUrl', { url })}
      onClick={() => onCopy(url)}
    >
      {copied ? t('platform.organizationDetail.urlCopied') : t('platform.organizationDetail.copyUrlAction')}
    </button>
  )
}

function locationStatusLabel(status: string, t: (key: string) => string): string | null {
  if (status === 'ACTIVE') return null
  if (status === 'SUSPENDED') return t('platform.organizationDetail.locationStatusSuspended')
  if (status === 'ARCHIVED') return t('platform.organizationDetail.locationStatusArchived')
  return status
}

function cityStatusLabel(status: string, t: (key: string) => string): string | null {
  if (status === 'ACTIVE') return null
  if (status === 'SUSPENDED') return t('platform.organizationDetail.cityStatusSuspended')
  if (status === 'ARCHIVED') return t('platform.organizationDetail.cityStatusArchived')
  return status
}

export default function OrganizationDetailPage() {
  const { t, i18n } = useTranslation()
  const handleApiError = usePlatformApiError()
  const { organizationId } = useParams<{ organizationId: string }>()
  const { platformRole } = useOutletContext<PlatformOutletContext>()
  const canManageLifecycle = isPlatformSuperadminRole(platformRole)

  const [detail, setDetail] = useState<PlatformOrganizationDetail | null>(null)
  const [status, setStatus] = useState<PageStatus>('loading')
  const [actionError, setActionError] = useState('')
  const [success, setSuccess] = useState('')
  const [processing, setProcessing] = useState<LifecycleAction | null>(null)
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const [copyError, setCopyError] = useState('')

  const organization: OrganizationResponse | null = detail?.organization ?? null

  const load = useCallback(() => {
    if (!organizationId) {
      setStatus('notFound')
      return Promise.resolve()
    }
    return getPlatformOrganizationDetail(organizationId)
      .then(data => {
        setDetail(data)
        setStatus('ready')
      })
      .catch((error: unknown) => {
        const kind = handleApiError(error)
        if (kind === null) return
        setStatus(kind === 'notFound' ? 'notFound' : kind === 'forbidden' ? 'forbidden' : 'error')
      })
  }, [organizationId, handleApiError])

  useEffect(() => {
    void load()
  }, [load])

  async function runAction(action: LifecycleAction) {
    if (!organization || processing) return
    const name = organization.name
    const confirmation = action === 'suspend'
      ? t('platform.organizationDetail.suspendConfirm', { name })
      : action === 'activate'
        ? t('platform.organizationDetail.activateConfirm', { name })
        : t('platform.organizationDetail.archiveConfirm', { name })
    if (!window.confirm(confirmation)) return

    setProcessing(action)
    setActionError('')
    setSuccess('')
    try {
      const updated = action === 'suspend'
        ? await suspendOrganization(organization.id)
        : action === 'activate'
          ? await activateOrganization(organization.id)
          : await archiveOrganization(organization.id)
      setDetail(prev => (prev ? { ...prev, organization: updated } : prev))
      setSuccess(t(`platform.organizationDetail.${action}Success`))
    } catch (error: unknown) {
      const kind = handleApiError(error)
      if (kind === null) return
      if (kind === 'notFound') {
        setStatus('notFound')
      } else if (kind === 'forbidden') {
        setActionError(t('platform.errors.forbidden'))
      } else if (error instanceof ApiError && error.code === 'INVALID_TRANSITION') {
        setActionError(t('platform.organizationDetail.invalidTransition'))
      } else {
        setActionError(t('platform.organizationDetail.actionError'))
      }
    } finally {
      setProcessing(null)
    }
  }

  async function handleCopyUrl(url: string) {
    setCopyError('')
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(url)
      window.setTimeout(() => setCopiedUrl(current => (current === url ? null : current)), 2000)
    } catch {
      setCopyError(t('platform.organizationDetail.copyError'))
    }
  }

  if (status === 'loading' && !detail) {
    return (
      <section className="page-section">
        <div className="page-loading"><div className="spinner" /></div>
      </section>
    )
  }

  if (status === 'notFound') {
    return (
      <section className="page-section">
        <h1 className="platform-page-title">{t('platform.organizationDetail.notFound')}</h1>
        <Link className="back-link" to={ROUTES.PLATFORM_ORGANIZATIONS}>
          {t('platform.organizationDetail.back')}
        </Link>
      </section>
    )
  }

  if (status === 'forbidden') {
    return (
      <section className="page-section">
        <div className="settings-card">
          <div className="form-error-banner" role="alert">
            {t('platform.errors.forbidden')}
          </div>
          <Link className="btn btn-ghost" to={ROUTES.PLATFORM_ORGANIZATIONS}>
            {t('platform.organizationDetail.back')}
          </Link>
        </div>
      </section>
    )
  }

  if (status === 'error' || !organization || !detail) {
    return (
      <section className="page-section">
        <div className="settings-card">
          <div className="form-error-banner" role="alert">
            {t('platform.organizationDetail.loadError')}
          </div>
          <Link className="btn btn-ghost" to={ROUTES.PLATFORM_ORGANIZATIONS}>
            {t('platform.organizationDetail.back')}
          </Link>
        </div>
      </section>
    )
  }

  const statusClass = organization.status.toLowerCase()
  const canSuspend = organization.status === 'ACTIVE'
  const canActivate = organization.status === 'SUSPENDED'
  const canArchive = organization.status !== 'ARCHIVED'
  const { counters, locations } = detail
  // A just-changed lifecycle status must not leave a stale "available" block
  // behind until the next full load; ORG_NOT_ACTIVE wins over the cached summary.
  const storefront = organization.status === 'ACTIVE'
    ? detail.storefront
    : { available: false, reason: 'ORG_NOT_ACTIVE' as const, urls: [] }

  const unavailableReasonText =
    storefront.reason === 'ORG_NOT_ACTIVE'
      ? t('platform.organizationDetail.reasonOrgNotActive')
      : storefront.reason === 'NO_ACTIVE_LOCATIONS'
        ? t('platform.organizationDetail.reasonNoActiveLocations')
        : t('platform.organizationDetail.reasonNoActiveCity')

  return (
    <section className="page-section page-section--wide">
      <Link className="back-link" to={ROUTES.PLATFORM_ORGANIZATIONS}>
        {t('platform.organizationDetail.back')}
      </Link>

      <div className="page-header">
        <div>
          <h1 className="platform-page-title">{organization.name}</h1>
          <p className="page-section-description">
            <code className="platform-code">{organization.slug}</code>
            {' · '}
            <span className={`status-badge status-badge--${statusClass}`}>
              {t(`platform.organizationStatus.${organization.status}`)}
            </span>
          </p>
        </div>
      </div>

      {success && <div className="form-success-banner" role="status">{success}</div>}
      {actionError && <div className="form-error-banner">{actionError}</div>}

      <div className="platform-detail-grid">
        <section className="settings-card">
          <h2>{t('platform.organizationDetail.mainInfo')}</h2>
          <dl className="platform-definition-list">
            <div>
              <dt>{t('platform.organizationDetail.name')}</dt>
              <dd>{organization.name}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.slug')}</dt>
              <dd><code className="platform-code">{organization.slug}</code></dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.id')}</dt>
              <dd><code className="platform-code">{organization.id}</code></dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.description')}</dt>
              <dd>{organization.description || t('platform.organizationDetail.notSet')}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.domain')}</dt>
              <dd>{organization.domain || t('platform.organizationDetail.notSet')}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.locale')}</dt>
              <dd>{organization.locale}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.timezoneOrg')}</dt>
              <dd>{organization.timezone}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.currency')}</dt>
              <dd>{organization.currency}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.pricePrecision')}</dt>
              <dd>{organization.pricePrecisionMode === 'HALF_UNIT'
                ? t('platform.organizationDetail.pricePrecisionHalfUnit')
                : t('platform.organizationDetail.pricePrecisionInteger')}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.createdAt')}</dt>
              <dd>{formatDate(organization.createdAt, i18n.language)}</dd>
            </div>
            <div>
              <dt>{t('platform.organizationDetail.updatedAt')}</dt>
              <dd>{formatDateTime(organization.updatedAt, i18n.language)}</dd>
            </div>
          </dl>
          <p className="field-hint">{t('platform.organizationDetail.timezoneOrgHint')}</p>
        </section>

        <section className="settings-card">
          <h2>{t('platform.organizationDetail.lifecycle')}</h2>
          <p className="page-section-description">
            {t('platform.organizationDetail.lifecycleHint')}
          </p>

          {!canManageLifecycle && (
            <p className="field-hint">{t('platform.organizationDetail.superadminOnly')}</p>
          )}

          {organization.status === 'ARCHIVED' && (
            <p className="field-hint">{t('platform.organizationDetail.archivedNote')}</p>
          )}

          {canManageLifecycle && organization.status !== 'ARCHIVED' && (
            <div className="platform-action-list">
              {canSuspend && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={processing !== null}
                  onClick={() => void runAction('suspend')}
                >
                  {processing === 'suspend'
                    ? t('platform.organizationDetail.processing')
                    : t('platform.organizationDetail.suspend')}
                </button>
              )}
              {canActivate && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={processing !== null}
                  onClick={() => void runAction('activate')}
                >
                  {processing === 'activate'
                    ? t('platform.organizationDetail.processing')
                    : t('platform.organizationDetail.activate')}
                </button>
              )}
              {canArchive && (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={processing !== null}
                  onClick={() => void runAction('archive')}
                >
                  {processing === 'archive'
                    ? t('platform.organizationDetail.processing')
                    : t('platform.organizationDetail.archive')}
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      <section className="settings-card">
        <h2>{t('platform.organizationDetail.publicPages')}</h2>
        {storefront.available ? (
          <>
            <p className="page-section-description">{t('platform.organizationDetail.publicPagesHint')}</p>
            <ul className="platform-url-list">
              {storefront.urls.map(entry => (
                <li key={entry.cityId} className="platform-url-row">
                  <div className="platform-url-main">
                    <code className="platform-code platform-url-text">{entry.url}</code>
                    <span className="field-hint">
                      {t('platform.organizationDetail.urlCity', {
                        city: entry.cityName,
                        slug: entry.citySlug,
                      })}
                    </span>
                    <span className="field-hint">
                      {t('platform.organizationDetail.urlLocations', { n: entry.locationCount })}
                    </span>
                  </div>
                  <div className="platform-url-actions">
                    <a
                      className="btn btn-ghost"
                      href={entry.path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('platform.organizationDetail.openPage')}
                    </a>
                    <CopyUrlButton
                      url={entry.url}
                      copied={copiedUrl === entry.url}
                      onCopy={url => void handleCopyUrl(url)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="field-hint">
            {t('platform.organizationDetail.storefrontUnavailable')}: {unavailableReasonText}
          </p>
        )}
        {copyError && <div className="form-error-banner" role="alert">{copyError}</div>}
      </section>

      <section className="settings-card">
        <h2>{t('platform.organizationDetail.locationsTitle')}</h2>
        {locations.length === 0 ? (
          <p className="field-hint">{t('platform.organizationDetail.locationsEmpty')}</p>
        ) : (
          <ul className="platform-location-list">
            {locations.map((loc: PlatformLocationSummary) => {
              const locStatus = locationStatusLabel(loc.status, t)
              const cityStatus = loc.city ? cityStatusLabel(loc.city.status, t) : null
              return (
                <li key={loc.id} className="platform-location-row">
                  <div className="platform-location-main">
                    <span className="platform-location-name">{loc.name}</span>
                    <span className="field-hint">
                      {loc.city
                        ? t('platform.organizationDetail.locationCity', {
                            city: loc.city.name,
                            slug: loc.city.slug,
                          })
                        : t('platform.organizationDetail.cityNone')}
                    </span>
                  </div>
                  <div className="platform-location-meta">
                    <span className="field-hint">
                      {t('platform.organizationDetail.locationTimezone', { timezone: loc.timezone })}
                    </span>
                    {locStatus && (
                      <span className="status-badge status-badge--suspended">{locStatus}</span>
                    )}
                    {cityStatus && (
                      <span className="status-badge status-badge--suspended">{cityStatus}</span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="settings-card">
        <h2>{t('platform.organizationDetail.countersTitle')}</h2>
        <dl className="platform-definition-list">
          <div>
            <dt>{t('platform.organizationDetail.categories')}</dt>
            <dd>
              {t('platform.organizationDetail.countersActiveOfTotal', {
                active: counters.categoriesActive,
                total: counters.categoriesTotal,
              })}
            </dd>
          </div>
          <div>
            <dt>{t('platform.organizationDetail.products')}</dt>
            <dd>
              {t('platform.organizationDetail.countersActiveOfTotal', {
                active: counters.productsActive,
                total: counters.productsTotal,
              })}
            </dd>
          </div>
        </dl>
        <p className="field-hint">{t('platform.organizationDetail.countersHint')}</p>
      </section>
    </section>
  )
}
